import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { animate, stagger } from 'animejs';
import { useAppStore } from '../store';
import { pushSupported } from '../utils/push';
import { openNotificationLink } from '../fed/deeplink';
import Btn from '../ui/Btn';

const TYPE_ICONS = {
  info:    { c: '#58a6ff', bg: 'rgba(88,166,255,0.07)',  border: 'rgba(88,166,255,0.18)',  icon: 'M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 9v5m0-8h.01' },
  success: { c: '#56d364', bg: 'rgba(86,211,100,0.07)',  border: 'rgba(86,211,100,0.18)',  icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3' },
  warning: { c: '#f5b942', bg: 'rgba(245,185,66,0.07)',  border: 'rgba(245,185,66,0.18)',  icon: 'M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4m0 4h.01' },
  alert:   { c: '#ff3b3b', bg: 'rgba(225,18,23,0.07)',   border: 'rgba(225,18,23,0.22)',   icon: 'M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4m0 4h.01' },
};

function relTime(iso, t) {
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60000)    return t ? t('notifications.justNow') : 'только что';
  if (d < 3600000)  return t ? t('notifications.minutesAgo', { n: Math.floor(d / 60000) }) : `${Math.floor(d / 60000)} мин. назад`;
  if (d < 86400000) return t ? t('notifications.hoursAgo', { n: Math.floor(d / 3600000) }) : `${Math.floor(d / 3600000)} ч. назад`;
  return t ? t('notifications.daysAgo', { n: Math.floor(d / 86400000) }) : `${Math.floor(d / 86400000)} д. назад`;
}

function NotifCard({ n, onRead, onDelete }) {
  const { t } = useTranslation();
  const cfg = TYPE_ICONS[n.type] || TYPE_ICONS.info;
  const label = t(`notifications.types.${n.type}`) || n.type;

  return (
    <div
      className="notif-card"
      onClick={() => { if (!n.read) onRead(n.id); if (n.link) openNotificationLink(n.link); }}
      style={{
        position: 'relative', display: 'flex', gap: 0, overflow: 'hidden',
        background: n.read ? 'var(--surface)' : cfg.bg,
        border: `1px solid ${n.read ? 'var(--line)' : cfg.border}`,
        borderRadius: 'var(--r-lg)',
        cursor: n.read && !n.link ? 'default' : 'pointer',
        transition: 'border-color .2s, background .2s, transform .2s, box-shadow .2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <div style={{ width: 4, background: n.read ? 'var(--line-strong)' : cfg.c, flexShrink: 0, borderRadius: '4px 0 0 4px', transition: 'background .2s' }} />

      <div style={{ flex: 1, padding: '18px 20px 16px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingRight: 34 }}>
          <img src="/fed-logo-icon-192.png" alt="" width="27" height="27" style={{
            borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
            boxShadow: '0 0 0 1px var(--line), 0 4px 12px rgba(0,0,0,.22)',
          }} />
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke={n.read ? 'var(--fg-faint)' : cfg.c} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d={cfg.icon} />
          </svg>
          <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: n.read ? 'var(--fg-faint)' : cfg.c, fontWeight: 700 }}>
            {label}
          </span>
          {!n.read && (
            <span style={{ marginLeft: 4, width: 7, height: 7, borderRadius: '50%',
              background: cfg.c, boxShadow: `0 0 8px ${cfg.c}`, flexShrink: 0,
              animation: 'c4blink 2s steps(1) infinite' }} />
          )}
        </div>

        <div className="display" style={{ fontSize: 17, color: n.read ? 'var(--fg-muted)' : 'var(--fg)',
          margin: '0 0 6px', fontWeight: 700, lineHeight: 1.25 }}>
          {n.title}
        </div>

        <p style={{ margin: '0 0 14px', fontSize: 14, color: 'var(--fg-soft)', lineHeight: 1.65, whiteSpace: 'pre-line' }}>
          {n.message}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)', letterSpacing: '0.06em' }}>
            {relTime(n.created_at)}
          </span>
          {n.read && (
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.1em' }}>
              · {t('notifications.read')}
            </span>
          )}
          {n.link && (
            <span className="mono" style={{ fontSize: 11, color: cfg.c, letterSpacing: '0.06em', marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {t('notifications.openLink')}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
            </span>
          )}
        </div>
      </div>

      <button
        onClick={e => { e.stopPropagation(); onDelete(n.id); }}
        style={{ position: 'absolute', top: 12, right: 12, width: 26, height: 26,
          border: '1px solid var(--line)', borderRadius: 6, background: 'transparent',
          color: 'var(--fg-faint)', cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', transition: 'color .15s, border-color .15s, background .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'var(--fg)'; e.currentTarget.style.background = 'var(--surface-2)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--fg-faint)'; e.currentTarget.style.background = 'transparent'; }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function Empty({ filter, t }) {
  return (
    <div style={{ padding: '64px 0', textAlign: 'center' }}>
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--fg-faint)"
        strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 20 }}>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <div className="display" style={{ fontSize: 22, margin: '0 0 8px', color: 'var(--fg-muted)' }}>
        {filter === 'unread' ? t('notifications.allRead') : t('notifications.empty')}
      </div>
      <p style={{ fontSize: 14, color: 'var(--fg-faint)', margin: 0, maxWidth: 300, marginInline: 'auto' }}>
        {filter === 'unread' ? t('notifications.emptyFiltered') : t('notifications.emptySub')}
      </p>
    </div>
  );
}

export default function NotificationsPage() {
  const { user, notifications, markNotificationRead, markAllNotificationsRead, deleteNotification,
    enablePushNotifications, disablePushNotifications, getPushNotificationState,
    go } = useAppStore();
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [pushState, setPushState] = useState({
    supported: pushSupported(),
    permission: pushSupported() ? Notification.permission : 'unsupported',
    subscribed: false,
    checking: true,
  });
  const listRef = useRef(null);
  const prevLen = useRef(0);

  const handleEnablePush = async () => {
    await enablePushNotifications();
    const state = await getPushNotificationState();
    setPushState({ ...state, checking: false });
  };

  const handleDisablePush = async () => {
    await disablePushNotifications();
    const state = await getPushNotificationState();
    setPushState({ ...state, checking: false });
  };

  const myNotifs = notifications.filter(n => n.to === 'all' || String(n.to) === String(user?.id));
  const shown = filter === 'unread' ? myNotifs.filter(n => !n.read) : myNotifs;
  const unread = myNotifs.filter(n => !n.read).length;

  useEffect(() => {
    let alive = true;
    getPushNotificationState().then((state) => {
      if (alive) setPushState({ ...state, checking: false });
    });
    return () => { alive = false; };
  }, [user?.id, getPushNotificationState]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('.notif-card');
    if (!cards.length || cards.length === prevLen.current) return;
    prevLen.current = cards.length;
    animate(cards, {
      opacity: [0, 1], translateX: [-20, 0],
      duration: 400, delay: stagger(60), ease: 'outCubic',
    });
  }, [shown.length, filter]);

  return (
    <div className="page-in" style={{ position: 'relative', zIndex: 1, padding: '44px 0 80px' }}>
      <div className="container" style={{ maxWidth: 760 }}>

        <button onClick={() => go('profile')} style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none',
          cursor: 'pointer', color: 'var(--fg-faint)', fontSize: 11, letterSpacing: '0.16em',
          padding: '6px 0', transition: 'color .18s, gap .18s', fontFamily: 'var(--font-mono)',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--fg-soft)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--fg-faint)'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          {t('notifications.toProfile')}
        </button>

        <div style={{ marginTop: 20, marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{t('notifications.cabinet')}</div>
              <h1 className="display" style={{ fontSize: 'clamp(26px,5vw,42px)', margin: 0, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {t('notifications.title')}
                {unread > 0 && (
                  <span style={{ fontSize: 16, background: 'var(--accent)', color: '#fff',
                    padding: '3px 11px', borderRadius: 20, fontFamily: 'var(--font-mono)',
                    boxShadow: '0 0 14px var(--accent-glow)', animation: 'c4blink 2.5s steps(1) infinite' }}>
                    {unread}
                  </span>
                )}
              </h1>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {pushState.checking ? (
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--fg-faint)' }}>
                  {t('notifications.pushChecking')}
                </span>
              ) : pushState.subscribed ? (
                <>
                  <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--fg-faint)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
                      {t('notifications.pushEnabled')}
                    </span>
                  </span>
                  <Btn variant="outline" size="sm" onClick={handleDisablePush}>
                    {t('notifications.disablePush')}
                  </Btn>
                </>
              ) : pushState.supported ? (
                <Btn variant="outline" size="sm" onClick={handleEnablePush}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M10 21h4"/>
                  </svg>
                  {t('notifications.enablePush')}
                </Btn>
              ) : null}
              {unread > 0 && (
                <Btn variant="outline" size="sm" onClick={markAllNotificationsRead}>
                  {t('notifications.markAll')}
                </Btn>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 0, marginTop: 28, borderBottom: '1px solid var(--line)' }}>
            {[['all', t('notifications.filterAll'), myNotifs.length], ['unread', t('notifications.filterUnread'), unread]].map(([k, l, cnt]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '10px 18px',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, textTransform: 'uppercase',
                letterSpacing: '0.04em', color: filter === k ? 'var(--fg)' : 'var(--fg-muted)',
                borderBottom: `2px solid ${filter === k ? 'var(--accent)' : 'transparent'}`,
                marginBottom: -1, transition: 'color .15s, border-color .15s',
                display: 'flex', alignItems: 'center', gap: 7,
              }}>
                {l}
                {cnt > 0 && (
                  <span className="mono" style={{
                    fontSize: 11, padding: '1px 7px', borderRadius: 10,
                    background: filter === k ? 'var(--accent)' : 'var(--surface-3)',
                    color: filter === k ? '#fff' : 'var(--fg-muted)',
                    transition: 'background .15s, color .15s',
                  }}>
                    {cnt}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 ? (
          <Empty filter={filter} t={t} />
        ) : (
          <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {shown.map(n => (
              <NotifCard key={n.id} n={n} onRead={markNotificationRead} onDelete={deleteNotification} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
